import React, { useCallback, useMemo } from 'react'
import { useWatch, useZionFormContext } from '@fs/zion-form'
import {
  Alert,
  Cell,
  FlowGrid,
  H2,
  H3,
  InlineButton,
  LabelSpacer,
  Paragraph,
  Radio,
  RadioGroup,
  Row,
  Separator,
  Subheading,
  useOverlay,
} from '@fs/zion-ui'
import { Trans, useTranslation } from 'react-i18next'
import { useRandomId } from '@fs/zion-frontend-friends'
import {
  getNestedOrFloatingElementById,
  useSlsStateContext,
  getDisplayLabelForFieldType,
  useSlsHighlightsContext,
} from '@fs/zion-sls'
import useNamesExperienceOptIn from '../../../../hooks/useNamesExperienceOptIn'
import { useCollectionContextProvider } from '../../../../providers/collectionProvider/CollectionProvider'
import { FIELDS } from '../../../../providers/collectionProvider/collectionConstants'
import { MAKE_NEW_RECORD } from '../../shared/utils/recordConstants'
import PrimaryPersonInfoOverlay from './PrimaryPersonInfoOverlay'
import FormStandardizedSexField from '../../../standardizedSexField/StandardizedSexField'
import HighlightButton from '../../panels/shared/HighlightButton'
import LinkNamesButton from '../../panels/shared/LinkNamesButton'
import { useLinkNamesSave } from '../../panels/linkNamesPanel/hooks/useLinkNamesSave'
import { handleInputFocus, handleInputBlur, prepareFieldForHighlights } from '../../shared/utils/highlightUtils'
import TextDetailField from './TextDetailField'

const PersonalDetailsForm = ({ person, splitScreenImageArk }) => {
  const [t] = useTranslation()
  const {
    formState: { errors, isSubmitted, defaultValues },
    setValue,
    getValues,
    resetField,
  } = useZionFormContext()
  const h = useSlsHighlightsContext()

  const { types: slsTypes, images, currentImageArk } = useSlsStateContext()
  const { restrictedFields } = useCollectionContextProvider()
  const isPrimaryRestricted = restrictedFields?.includes(FIELDS.PRIMARY)
  const notSureOverlay = useOverlay()
  const { isOptedIn } = useNamesExperienceOptIn()

  // Extract default field values for sex to determine if the field was pre-populated
  const defaultSexField = useMemo(() => defaultValues?.SEX_CODE?.value, [defaultValues])

  const [primaryPersonValue, recordId, sexField] = useWatch({
    name: ['primaryPerson', 'recordId', 'SEX_CODE'],
  })
  const primaryPersonHeadingId = `primary-person-heading-${useRandomId()}`
  const isNewPerson = defaultValues?.added ?? true

  const handlePrimaryPersonChange = useCallback(
    ({ target: { value } }) => {
      setValue('primaryPerson', value === 'true', { shouldDirty: true, shouldValidate: true })
    },
    [setValue]
  )

  // Check if the household has any other primary person
  const currentPersonId = useWatch({ name: 'id' })

  const householdHasNoPrimary = useMemo(() => {
    if (primaryPersonValue !== false) return false
    if (recordId === MAKE_NEW_RECORD) return true
    if (!recordId) return false
    const record = getNestedOrFloatingElementById(images, recordId)
    if (!record) return false
    const hasOtherPrimary = record?.subElements?.some(
      (el) => el.elementType === 'PERSON' && el.primary === true && el.id !== currentPersonId
    )
    return !hasOtherPrimary
  }, [primaryPersonValue, recordId, images, currentPersonId])

  // Use the hook for handling link names save
  const { handleLinkNamesSave } = useLinkNamesSave({
    getValues,
    resetField,
    images,
    currentImageArk,
  })

  // Handlers to manage highlight hover on input focus/blur - using utility functions
  const onInputFocus = useCallback((fieldIdPath) => handleInputFocus(fieldIdPath, getValues, h), [getValues, h])
  const onInputBlur = useCallback(() => handleInputBlur(h), [h])

  const handleHighlightSave = useCallback(
    (fieldName) => (data) => {
      if (data?.newFieldId && data?.newFieldValue) {
        const currentFieldData = getValues(fieldName)
        const linkedPersonIds =
          data?.selectedPersonIds?.map((id) => ({
            partitionKey: currentImageArk,
            id,
          })) || []

        resetField(fieldName, {
          defaultValue: {
            ...currentFieldData,
            id: data.newFieldId,
            value: data.newFieldValue,
            removed: false,
            dispatches: data?.dispatches || [],
            shared: data?.selectedPersonIds?.length > 1, // Lock the field if it's associated with multiple persons
            linkedPersonIds,
          },
          keepDirty: false,
          keepErrors: false,
          keepTouched: false,
        })

        setValue(`${fieldName}.value`, data?.newFieldValue)
      }
    },
    [resetField, getValues, setValue, currentImageArk]
  )

  return (
    <FlowGrid orientation="vertical" columnCount={1} gutters="xs">
      <H2 size="H6">{t('essential-info-personal-details', 'Personal Details')}</H2>
      <>
        <Row gutters="xxs">
          {/* Adding this check to prevent errors in case person data is not available, as the standardized sex field
        relies on person data to function properly. This input will throw an error if the user changes images with the form in edit mode
        because the person will become undefined */}
          {person && (
            <FormStandardizedSexField
              fieldName="SEX_CODE.value"
              addedSexCode={!defaultSexField ? { id: 'SEX_CODE' } : null}
              person={person}
              autosuggestProps={{
                // Zion hides labels whose root is a component (only skeletons pass), so Trans needs a DOM wrapper.
                label: (
                  <span>
                    <Trans
                      i18nKey="essentialInformationForm.requiredFieldLabel"
                      defaults="{label} <0>({comment})</0>"
                      values={{
                        label: getDisplayLabelForFieldType('SEX_CODE', slsTypes),
                        comment: t('essentialInformationForm.required', 'Required'),
                      }}
                      components={[<em key="comment" />]}
                    />
                  </span>
                ),
                // Not native `required`: ZionForm doesn't set noValidate, so the browser bubble would show too.
                'aria-required': true,
                onFocus: () => onInputFocus('SEX_CODE.id'),
                onBlur: onInputBlur,
                'data-testid': 'sex-code-field',
                ignoreTouchedForWarnings: true,
                dense: true,
              }}
              useDropdown
              growX
            />
          )}
          <Cell>
            <LabelSpacer />
            <HighlightButton
              field={prepareFieldForHighlights(getValues, 'SEX_CODE', 'SEX_CODE', slsTypes, images)}
              splitScreenImageArk={splitScreenImageArk}
              buttonProps={{
                'data-testid': 'sex-code-highlight-button',
              }}
              linkName="essential-information-sex-code-highlight"
              onSave={handleHighlightSave('SEX_CODE')}
              saveToSls={!isNewPerson && !getValues('SEX_CODE.added')} // Only save to SLS if this is not a new person and the field isn't newly added
              growX
              iconOnly
            />
          </Cell>
        </Row>

        {isOptedIn && person && sexField?.shared && !isNewPerson && (
          <LinkNamesButton
            person={person}
            field={sexField}
            onSave={handleLinkNamesSave('SEX_CODE')}
            linkedPersonIds={sexField?.linkedPersonIds || []}
          />
        )}
      </>

      <TextDetailField
        fieldType="RACE_OR_COLOR"
        testIdPrefix="race"
        person={person}
        splitScreenImageArk={splitScreenImageArk}
        isNewPerson={isNewPerson}
        onHighlightSave={handleHighlightSave('RACE_OR_COLOR')}
        onLinkNamesSave={handleLinkNamesSave('RACE_OR_COLOR')}
      />
      <TextDetailField
        fieldType="AGE"
        testIdPrefix="age"
        person={person}
        splitScreenImageArk={splitScreenImageArk}
        isNewPerson={isNewPerson}
        onHighlightSave={handleHighlightSave('AGE')}
        onLinkNamesSave={handleLinkNamesSave('AGE')}
      />

      <>
        <H3 size="H6" id={primaryPersonHeadingId}>
          {t('essentialInformationForm.primaryPerson.title', 'Primary Person')}
          <Subheading>
            <i>{t('essentialInformationForm.required', 'Required')}</i>
          </Subheading>
        </H3>
        <Separator size="nano" />
        <Paragraph size="sm">
          {t('essentialInformationForm.primaryPerson.label', 'Is the document primarily about this person?')}{' '}
          <InlineButton
            onClick={notSureOverlay.open}
            linkName="primary-person-not-sure"
            data-testid="primary-person-not-sure-button"
          >
            <em>{t('essentialInformationForm.primaryPerson.notSure', 'Not sure?')}</em>
          </InlineButton>
        </Paragraph>
        <RadioGroup
          role="radiogroup"
          aria-labelledby={primaryPersonHeadingId}
          aria-required="true"
          data-testid="primary-person-radio-group"
          orientation="horizontal"
          errorMessage={isSubmitted && errors?.primaryPerson?.message}
          disabled={isPrimaryRestricted}
          dense
        >
          <Radio
            name="primaryPerson"
            value="true"
            checked={primaryPersonValue === true}
            label={t('essentialInformationForm.primaryPerson.yes', 'Yes')}
            onChange={handlePrimaryPersonChange}
          />
          <Radio
            name="primaryPerson"
            value="false"
            checked={primaryPersonValue === false}
            label={t('essentialInformationForm.primaryPerson.no', 'No')}
            onChange={handlePrimaryPersonChange}
          />
        </RadioGroup>

        {householdHasNoPrimary && (
          <Alert type="warning" data-testid="no-primary-person-warning">
            {t(
              'essentialInformationForm.primaryPerson.noPrimaryWarning',
              'This household will have no primary person.'
            )}
          </Alert>
        )}

        <PrimaryPersonInfoOverlay overlay={notSureOverlay} />
      </>
    </FlowGrid>
  )
}

export default PersonalDetailsForm
